import type { ReactNode } from "react";

import { useT } from "../i18n";
import { useDialogoDeAtalhos } from "./useAtalhos";

/** O que a tela precisa para sair da carreira. */
export interface Saida {
  /**
   * Mostra "Sair sem gravar".
   *
   * Só na carreira deste aparelho com o salvamento automático desligado: com
   * ele ligado não há o que perder, e na carreira da conta o servidor já tem
   * cada rodada.
   */
  semGravar: boolean;
  /** Onde a carreira foi gravada pela última vez, ou nulo se nunca foi. */
  gravado: { season: number; round: number } | null;
  aoGravarESair: () => void;
  aoSairSemGravar: () => void;
}

/**
 * Sair da carreira: gravar e sair, sair sem gravar ou ficar.
 *
 * Abre pelo Esc na tela inicial do hub e pelo botão das Configurações. A liga
 * online não passa por aqui: lá cada decisão já está no servidor, e o hub dela
 * tem o próprio "Sair da liga".
 */
export function DialogoDeSaida({ saida, aoFechar }: { saida: Saida; aoFechar: () => void }): ReactNode {
  const t = useT();
  useDialogoDeAtalhos({ nome: "Saida", nivel: 30, aoFechar });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("saida.titulo")}
      onClick={aoFechar}
      className="dialogo-de-saida"
    >
      <div className="card" onClick={(evento) => evento.stopPropagation()}>
        <h3 className="label" style={{ margin: "0 0 4px", color: "var(--primary)" }}>
          {t("saida.titulo")}
        </h3>
        <p className="label-muted" style={{ margin: "0 0 14px" }}>
          {saida.gravado
            ? t("saida.gravadoEm", { temporada: saida.gravado.season, rodada: saida.gravado.round })
            : t("saida.naoGravado")}
        </p>
        <div className="opcoes-de-saida">
          <button autoFocus onClick={saida.aoGravarESair}>
            <IconeGravar />
            <span>{t("saida.gravarESair")}</span>
          </button>
          {saida.semGravar && (
            <button className="perigo" onClick={saida.aoSairSemGravar}>
              <IconeSair />
              <span>
                {t("saida.sairSemGravar")}
                {saida.gravado && <small>{t("saida.voltaA", { rodada: saida.gravado.round })}</small>}
              </span>
            </button>
          )}
          <button onClick={aoFechar}>
            <IconeFechar />
            <span>{t("comum.cancelar")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const TRACO = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function IconeGravar(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" {...TRACO}>
      <path d="M5 3h11l3 3v15H5Z" />
      <path d="M8 3v5h7V3M8 21v-7h8v7" />
    </svg>
  );
}

function IconeSair(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" {...TRACO}>
      <path d="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h9" />
    </svg>
  );
}

function IconeFechar(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" {...TRACO}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
